import { useState, useEffect, useRef } from 'react'
import type { ConsultationMessage } from '@creanizer/types'
import { Loader2, ArrowDown } from 'lucide-react'
import { Button } from '@creanizer/ui'
import { ChatMessage } from './ChatMessage'

interface ChatContainerProps {
  messages: ConsultationMessage[]
  sessionId: string
  isStreaming: boolean
  onRetryUserMessage?: (messageId: string) => void
  onRegenerateAIMessage?: (messageId: string) => void
}

export function ChatContainer({
  messages,
  sessionId,
  isStreaming,
  onRetryUserMessage,
  onRegenerateAIMessage
}: ChatContainerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const prevMessageCountRef = useRef(messages.length)

  // メッセージ数が増えた時のみ自動スクロール（設定変更時のスクロールを防止）
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = messages.length
  }, [messages.length])

  // スクロール位置を監視
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      // 最下部から100px以上離れたらボタンを表示
      setShowScrollToBottom(distanceFromBottom > 100)
    }

    container.addEventListener('scroll', handleScroll)

    // 初回チェック
    handleScroll()

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleScrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-1 py-2 sm:p-4 space-y-3 sm:space-y-4 relative">
      {messages.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <p className="text-lg mb-4">創作の相談を始めましょう</p>
          <div className="space-y-2 text-sm">
            <p>例えば以下のような相談ができます：</p>
            <ul className="space-y-1 text-left max-w-md mx-auto">
              <li>• キャラクターの設定について</li>
              <li>• ストーリーの展開について</li>
              <li>• 世界観の構築について</li>
              <li>• 創作の悩みや行き詰まりについて</li>
              <li>• 作品の改善点について</li>
              <li>• ジャンルの特徴や書き方について</li>
            </ul>
          </div>
        </div>
      ) : (
        <>
          {messages.map((message, index) => {
            // ボタンの表示条件
            const canRetry = message.role === 'user'
            const canRegenerate = message.role === 'assistant'
            
            return (
              <ChatMessage 
                key={message.id} 
                message={message}
                sessionId={sessionId}
                showRetryButton={canRetry}
                showRegenerateButton={canRegenerate}
                onRetry={canRetry ? () => onRetryUserMessage?.(message.id) : undefined}
                onRegenerate={canRegenerate ? () => onRegenerateAIMessage?.(message.id) : undefined}
              />
            )
          })}
        </>
      )}
      <div ref={messagesEndRef} />

      {isStreaming && (
        <div className="flex items-center space-x-2 p-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            応答を生成中...
          </span>
        </div>
      )}

      {/* 最新に移動ボタン */}
      {showScrollToBottom && (
        <div className="sticky bottom-4 right-4 flex justify-end pointer-events-none">
          <Button
            onClick={handleScrollToBottom}
            className="pointer-events-auto rounded-full shadow-lg h-12 w-12 p-0"
            variant="default"
            size="icon"
            title="最新のメッセージに移動"
          >
            <ArrowDown className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  )
}